import { IAdvancedFilterGroup } from '../../settings-new/models';
import { ISearchableFields } from '../../../shared/shared-models/type-sense/default-searchable-fields';
import { IStore } from '../../../shared/shared-models/store/store';
import {
  StockItem,
  StockItemUpdateFieldValue,
  StockItemUpdateObj,
  VatRates,
} from '../../../shared-utilities/models-old/datastructures';
import {
  calculateGPFromPrice,
  calculatePriceFromGpValue,
} from '../../../shared/shared-utils/calculations/calculations-stock';
import { convertToDecimal } from '../../../shared/shared-utils/number/number.utils';
import { IPriceBand } from '../../../shared/shared-models/price-banding/price-band';
import { snapPriceToPriceBands } from '../../../shared/shared-utils/price/price-banding.utils';
import { stockManagerFieldValuesAreEqual } from './stock-manager-barcode.utils';
import {
  BULK_CHANGE_BLANK_SUPPLIER_KEY,
  BULK_CHANGE_COLUMNS,
  BULK_CHANGE_LINKED_PRICE_FIELDS,
  BULK_CHANGE_MAX_ITEMS,
  BULK_CHANGE_NON_STOCK_CATEGORY,
  BULK_CHANGE_WRITE_BATCH_SIZE,
  BulkChangeOperation,
  CURRENT_GP_BULK_CHANGE_EXCLUSION_NOTE,
  IBulkChangeColumn,
  IBulkChangeDraftRow,
  IBulkChangeExcludedItem,
  IBulkChangeRow,
} from '../models/stock-manager-bulk-change.model';

const PRICE_BANDING_EXCEPTION_TAG = 'price_banding_exception';

const OPERATION_LABELS: Record<BulkChangeOperation, string> = {
  replace: 'Replace value',
  prefix: 'Amend with prefix',
  suffix: 'Amend with suffix',
};

/**
 * Returns the display label for a bulk-change operation.
 */
export function bulkChangeOperationLabel(operation: BulkChangeOperation): string {
  return OPERATION_LABELS[operation];
}

/**
 * Returns the column definition for a field, when that field can be bulk-changed.
 */
export function getBulkChangeColumn(field: string): IBulkChangeColumn | undefined {
  return BULK_CHANGE_COLUMNS.find((column: IBulkChangeColumn) => column.field === field);
}

/**
 * True when an advanced filter is applied or at least one refinement is selected.
 * The search box alone does not count.
 */
export function hasStockManagerBulkChangeScope(
  filters: Record<string, ISearchableFields> | null | undefined,
  advancedFilter: IAdvancedFilterGroup | null | undefined,
): boolean {
  if ((advancedFilter?.filterGroup?.filtersAndGroups?.length ?? 0) > 0) {
    return true;
  }
  if (!filters) {
    return false;
  }
  return Object.values(filters).some(
    (group: ISearchableFields) =>
      !!group && Object.values(group).some((field) => field?.value === true),
  );
}

/**
 * True when bulk changes may be started for the current result set.
 */
export function isStockManagerBulkChangeAvailable(
  totalItems: number,
  filters: Record<string, ISearchableFields> | null | undefined,
  advancedFilter: IAdvancedFilterGroup | null | undefined,
): boolean {
  return (
    hasStockManagerBulkChangeScope(filters, advancedFilter) &&
    totalItems > 0 &&
    totalItems < BULK_CHANGE_MAX_ITEMS
  );
}

/**
 * Tooltip for the bulk-changes button.
 */
export function stockManagerBulkChangeTooltip(
  totalItems: number,
  filters: Record<string, ISearchableFields> | null | undefined,
  advancedFilter: IAdvancedFilterGroup | null | undefined,
): string {
  if (!isStockManagerBulkChangeAvailable(totalItems, filters, advancedFilter)) {
    return 'Bulk changes are available when a filter or refinement is applied and fewer than 1,000 items match.';
  }
  const noun = totalItems === 1 ? 'item' : 'items';
  return `Apply the same changes to ${totalItems} filtered ${noun}.`;
}

/**
 * Fields that cannot be chosen because they are already used, or because the linked price field is used.
 */
export function bulkChangeFieldsBlockedByRows(
  rows: IBulkChangeDraftRow[],
  currentRowId: string,
): Set<string> {
  const blocked = new Set<string>();
  rows.forEach((row: IBulkChangeDraftRow) => {
    if (!row.field || row.id === currentRowId) {
      return;
    }
    blocked.add(row.field);
    if (BULK_CHANGE_LINKED_PRICE_FIELDS.includes(row.field)) {
      BULK_CHANGE_LINKED_PRICE_FIELDS.forEach((linkedField: string) => blocked.add(linkedField));
    }
  });
  return blocked;
}

/**
 * True when every draft row has a column, an operation, and a value.
 */
export function isBulkChangeDraftComplete(rows: IBulkChangeDraftRow[]): boolean {
  return (
    rows.length > 0 && rows.every((row: IBulkChangeDraftRow) => isBulkChangeDraftRowComplete(row))
  );
}

/**
 * Converts complete draft rows into confirmed change rows.
 */
export function toBulkChangeRows(
  rows: IBulkChangeDraftRow[],
  valueLabels: Record<string, string>,
): IBulkChangeRow[] {
  return rows.map((row: IBulkChangeDraftRow) => {
    const column = getBulkChangeColumn(row.field);
    const value = draftRowValue(row);
    const valueLabel = valueLabels[row.id] ?? String(value);
    return {
      field: row.field,
      operation: row.operation,
      value,
      summary: describeBulkChange(
        column?.label ?? row.field,
        row.operation,
        valueLabel,
        row.field,
        row.field === 'nominalGP' && row.usePriceBanding,
      ),
      usePriceBanding: row.field === 'nominalGP' && row.usePriceBanding,
    };
  });
}

/**
 * Builds the STOCK_UPDATE payload for the matched items. Items with no actual change are omitted.
 */
export function buildBulkStockUpdates(
  items: StockItem[],
  rows: IBulkChangeRow[],
  store: IStore,
  vatRates?: VatRates,
  priceBands: IPriceBand[] = [],
): Record<string, StockItemUpdateObj> {
  const updates: Record<string, StockItemUpdateObj> = {};
  items.forEach((item: StockItem) => {
    const code = String(item.code ?? '').trim();
    if (!code) {
      return;
    }
    const update = buildUpdateForItem(item, rows, store, vatRates, priceBands);
    if (update) {
      updates[code] = update;
    }
  });
  return updates;
}

/**
 * Splits a stock update into batches. Each batch is sent with the same stock update
 * call the grid uses, so each batch gets its own processing message.
 */
export function chunkBulkStockUpdates(
  updates: Record<string, StockItemUpdateObj>,
  batchSize: number = BULK_CHANGE_WRITE_BATCH_SIZE,
): Record<string, StockItemUpdateObj>[] {
  const codes = Object.keys(updates);
  const batches: Record<string, StockItemUpdateObj>[] = [];
  for (let index = 0; index < codes.length; index += batchSize) {
    const batch: Record<string, StockItemUpdateObj> = {};
    codes.slice(index, index + batchSize).forEach((code: string) => {
      batch[code] = updates[code];
    });
    batches.push(batch);
  }
  return batches;
}

function isBulkChangeDraftRowComplete(row: IBulkChangeDraftRow): boolean {
  const column = getBulkChangeColumn(row.field);
  if (!column || !column.operations.includes(row.operation)) {
    return false;
  }
  if (column.kind === 'text') {
    if (row.operation === 'replace' && row.allowBlank) {
      return true;
    }
    return row.textValue.trim().length > 0;
  }
  if (column.kind === 'number') {
    return isValidBulkNumber(row.field, row.numberValue);
  }
  if (column.kind === 'tags' || column.kind === 'onHold') {
    return row.multiValues.length > 0;
  }
  return row.singleValue.length > 0;
}

function isValidBulkNumber(field: string, rawValue: string): boolean {
  if (rawValue.trim() === '') {
    return false;
  }
  const value = Number(rawValue);
  if (!Number.isFinite(value)) {
    return false;
  }
  if (field === 'nominalGP' || field === 'recommendedGP') {
    return value >= 0 && value < 100;
  }
  if (field === 'sellPriIncl1') {
    return value >= 0;
  }
  return true;
}

function draftRowValue(row: IBulkChangeDraftRow): string | number | string[] {
  const column = getBulkChangeColumn(row.field);
  if (!column) {
    return '';
  }
  if (column.kind === 'text') {
    if (row.operation === 'replace' && row.allowBlank) {
      return '';
    }
    return row.textValue;
  }
  if (column.kind === 'number') {
    return Number(row.numberValue);
  }
  if (column.kind === 'tags') {
    return [...row.multiValues];
  }
  if (column.kind === 'onHold') {
    return row.multiValues.reduce((total: number, key: string) => total + Number(key), 0);
  }
  if (row.singleValue === BULK_CHANGE_BLANK_SUPPLIER_KEY) {
    return '';
  }
  if (column.kind === 'subDepartment' && row.singleValue.includes('::')) {
    return row.singleValue.split('::')[1] ?? row.singleValue;
  }
  return row.singleValue;
}

function describeBulkChange(
  label: string,
  operation: BulkChangeOperation,
  valueLabel: string,
  field: string,
  usePriceBanding: boolean = false,
): string {
  if (operation === 'prefix') {
    return `${label}: add prefix "${valueLabel}"`;
  }
  if (operation === 'suffix') {
    return `${label}: add suffix "${valueLabel}"`;
  }
  if (field === 'sellPriIncl1') {
    return `${label}: replace with ${valueLabel}. Current GP % will be recalculated for each item.`;
  }
  if (field === 'nominalGP' && usePriceBanding) {
    return (
      `${label}: replace with ${valueLabel}. Retail price will be calculated for each item and snapped to price bands. ` +
      `Current GP % may change slightly after banding. ${CURRENT_GP_BULK_CHANGE_EXCLUSION_NOTE}`
    );
  }
  if (field === 'nominalGP') {
    return (
      `${label}: replace with ${valueLabel}. Retail Price will be recalculated for each item. ` +
      CURRENT_GP_BULK_CHANGE_EXCLUSION_NOTE
    );
  }
  return `${label}: replace with ${valueLabel}`;
}

function buildUpdateForItem(
  item: StockItem,
  rows: IBulkChangeRow[],
  store: IStore,
  vatRates: VatRates | undefined,
  priceBands: IPriceBand[],
): StockItemUpdateObj | null {
  const changes: Record<string, StockItemUpdateFieldValue> = {};
  const originals: Record<string, StockItemUpdateFieldValue> = {};

  const setChange = (
    field: string,
    nextValue: StockItemUpdateFieldValue,
    previousValue: unknown,
  ): void => {
    if (stockManagerFieldValuesAreEqual(field, nextValue, previousValue)) {
      return;
    }
    changes[field] = nextValue;
    originals[field] = (previousValue ?? '') as StockItemUpdateFieldValue;
  };

  rows.forEach((row: IBulkChangeRow) => {
    if (row.field === 'sellPriIncl1' || row.field === 'nominalGP') {
      if (row.field === 'nominalGP' && isExcludedFromCurrentGpBulkChange(item)) {
        return;
      }
      const linked = applyLinkedPriceGp(
        item,
        row.field,
        Number(row.value),
        store,
        vatRates,
        row.field === 'nominalGP' && row.usePriceBanding === true,
        priceBands,
      );
      setChange('sellPriIncl1', linked.sellPriIncl1, item.sellPriIncl1);
      setChange('nominalGP', linked.nominalGP, item.nominalGP);
      return;
    }
    if (row.field === '_tags') {
      const currentTags = Array.isArray(item._tags) ? item._tags.map(String) : [];
      const disabledTag = currentTags.includes('disabled') ? ['disabled'] : [];
      const nextTags = [...new Set([...(row.value as string[]), ...disabledTag])];
      setChange('_tags', nextTags, currentTags);
      return;
    }
    const column = getBulkChangeColumn(row.field);
    if (!column) {
      return;
    }
    if (column.kind === 'text') {
      const currentText = String(item[row.field as keyof StockItem] ?? '');
      setChange(
        row.field,
        applyTextChange(currentText, row.operation, String(row.value ?? '')),
        currentText,
      );
      return;
    }
    if (column.kind === 'number' || column.kind === 'onHold') {
      const previous = item[row.field as keyof StockItem];
      setChange(row.field, Number(row.value), previous);
      return;
    }
    const previous = item[row.field as keyof StockItem];
    setChange(row.field, String(row.value ?? ''), previous);
  });

  if (Object.keys(changes).length === 0) {
    return null;
  }

  const details: NonNullable<StockItemUpdateObj['details']> = {};
  details[1] = item.desc ?? '';
  return {
    n: changes,
    o: originals,
    details,
  };
}

/**
 * Reads a message from a stock-update rejection or a thrown error.
 */
export function bulkChangeErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const record = error as { message?: string; error?: { message?: string } };
    return record.error?.message || record.message || 'Bulk stock changes could not be applied.';
  }
  return 'Bulk stock changes could not be applied.';
}

function applyTextChange(
  currentValue: string,
  operation: BulkChangeOperation,
  value: string,
): string {
  if (operation === 'prefix') {
    return `${value}${currentValue}`;
  }
  if (operation === 'suffix') {
    return `${currentValue}${value}`;
  }
  return value;
}

/**
 * Current GP cannot set a selling price when latest cost is R0, and non-stock items are left unchanged.
 */
export function isExcludedFromCurrentGpBulkChange(item: StockItem): boolean {
  return currentGpExclusionReasons(item).length > 0;
}

/**
 * True when one of the rows sets Current GP %.
 */
export function bulkChangeRowsIncludeCurrentGp(rows: IBulkChangeRow[]): boolean {
  return rows.some((row: IBulkChangeRow) => row.field === 'nominalGP');
}

/**
 * Lists the matched items a Current GP % change will skip, with the reason for each.
 * Returns an empty list when no row sets Current GP %.
 */
export function getCurrentGpBulkChangeExcludedItems(
  items: StockItem[],
  rows: IBulkChangeRow[],
): IBulkChangeExcludedItem[] {
  if (!bulkChangeRowsIncludeCurrentGp(rows)) {
    return [];
  }
  return items
    .map((item: StockItem): IBulkChangeExcludedItem | null => {
      const reasons = currentGpExclusionReasons(item);
      if (reasons.length === 0) {
        return null;
      }
      const latestCost = Number(item.latestCost);
      return {
        code: String(item.code ?? '').trim(),
        desc: item.desc ?? '',
        latestCost: Number.isFinite(latestCost) ? latestCost : 0,
        reason: reasons.join(', '),
      };
    })
    .filter((item: IBulkChangeExcludedItem | null): item is IBulkChangeExcludedItem => !!item?.code)
    .sort((left: IBulkChangeExcludedItem, right: IBulkChangeExcludedItem) =>
      left.code.localeCompare(right.code),
    );
}

function currentGpExclusionReasons(item: StockItem): string[] {
  const reasons: string[] = [];
  const latestCost = Number(item.latestCost);
  if (!Number.isFinite(latestCost) || latestCost < 0.005) {
    reasons.push('Latest cost is R0');
  }
  if (Number(item.category) === BULK_CHANGE_NON_STOCK_CATEGORY) {
    reasons.push('Non-stock item (category 2)');
  }
  return reasons;
}

function applyLinkedPriceGp(
  item: StockItem,
  field: 'sellPriIncl1' | 'nominalGP',
  rawValue: number,
  store: IStore,
  vatRates: VatRates | undefined,
  usePriceBanding: boolean,
  priceBands: IPriceBand[],
): { sellPriIncl1: number; nominalGP: number } {
  const working: StockItem = { ...item };
  if (field === 'nominalGP') {
    const gp = rawValue >= 100 ? 99.99 : convertToDecimal(rawValue, 2);
    working.nominalGP = gp;
    let price = convertToDecimal(calculatePriceFromGpValue(store, working, vatRates), 2);
    if (usePriceBanding && !hasPriceBandingException(item)) {
      const snapped = Number(snapPriceToPriceBands(price, priceBandsForItem(item, priceBands)).toFixed(2));
      if (snapped !== price) {
        price = snapped;
        working.sellPriIncl1 = price;
        const updated = calculateGPFromPrice(working, store, 2, vatRates);
        const bandedGp = Number.isFinite(updated.nominalGP) ? updated.nominalGP : gp;
        return { nominalGP: bandedGp, sellPriIncl1: price };
      }
    }
    return { nominalGP: gp, sellPriIncl1: price };
  }
  const price = convertToDecimal(rawValue, 2);
  working.sellPriIncl1 = price;
  const updated = calculateGPFromPrice(working, store, 2, vatRates);
  const gp = Number.isFinite(updated.nominalGP) ? updated.nominalGP : Number(item.nominalGP ?? 0);
  return { sellPriIncl1: price, nominalGP: gp };
}

/**
 * Keeps bands that apply to this item's department and sub-department.
 * A band with no department list applies to every department.
 */
function priceBandsForItem(item: StockItem, priceBands: IPriceBand[]): IPriceBand[] {
  const department = String(item.dep ?? '');
  const subDepartment = String(item.subDep ?? '');
  return priceBands.filter((band: IPriceBand): boolean => {
    const departmentMatch = !band.departs?.length || band.departs.includes(department);
    const subDepartmentMatch = !band.subDeparts?.length || band.subDeparts.includes(subDepartment);
    return departmentMatch && subDepartmentMatch;
  });
}

function hasPriceBandingException(item: StockItem): boolean {
  return Array.isArray(item._tags) && item._tags.map(String).includes(PRICE_BANDING_EXCEPTION_TAG);
}
